import { push, replace, RouterAction } from 'connected-react-router'
import { Location } from 'history'
import React from 'react'
import { useSelector } from 'react-redux'
import { matchPath, Redirect } from 'react-router'
import { Link } from 'react-router-dom'
import { ThunkDispatch } from 'redux-thunk'
import { translateSingleString } from 'src/localization/Trns'
import {
  authedFlyoutRoutePropsWithBackgroundLocations,
  unauthedRightFlyoutRoutePropsWithBackgroundLocations,
} from 'src/navigation/modalBackgrounds'
import { LocationState, RootState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { CSSProp } from 'styled-components'

const toCoalesceToLocation = (
  to: string | Location<LocationState>,
): Location<LocationState> =>
  typeof to === 'string'
    ? {
        pathname: to,
        search: '',
        hash: '',
        state: {},
      }
    : to

const calculateNextLocation = (
  toLocation: Location<LocationState>,
  calculatedBackgroundLocation: Location<LocationState>,
): Location<LocationState> => {
  const nextBackgroundLocation =
    authedFlyoutRoutePropsWithBackgroundLocations.some(
      (r) =>
        matchPath(toLocation.pathname, r.routeProps) &&
        !r.exceptionRouteProps?.some((exc) =>
          matchPath(toLocation.pathname, exc),
        ),
    ) ||
    unauthedRightFlyoutRoutePropsWithBackgroundLocations.some(
      (r) =>
        matchPath(toLocation.pathname, r.routeProps) &&
        !r.exceptionRouteProps?.some((exc) =>
          matchPath(toLocation.pathname, exc),
        ),
    )
      ? calculatedBackgroundLocation
      : toLocation

  return {
    ...toLocation,
    state: {
      ...toLocation.state,
      backgroundLocation: nextBackgroundLocation,
    },
  }
}

export type LnkProps = LnkPropsShared & LinkTypeProps & AriaProps

interface LnkPropsShared {
  className?: string
  id?: string
  testId?: string
  css?: CSSProp
  tabIndex?: number
}

export type LinkTypeProps = LinkProps | AnchorProps

interface LinkProps {
  to: string | Location<LocationState>
  href?: undefined
  anchorProps?: React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  >
  onClick?: React.MouseEventHandler
  onKeyPress?: React.KeyboardEventHandler
}

interface AnchorProps {
  to?: undefined
  href: string
  anchorProps?: React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  >
  onClick?: React.MouseEventHandler
  onKeyPress?: React.KeyboardEventHandler
}

export type AriaProps = LnkPropsAriaSource | LnkPropsAriaLabel

interface LnkPropsAriaSource {
  ariaLabelSource: string
  ariaLabel?: undefined
}

interface LnkPropsAriaLabel {
  ariaLabel: string
  ariaLabelSource?: undefined
}

const Lnk: React.FC<React.PropsWithChildren<LnkProps>> = (
  props,
): React.ReactElement => {
  const toLocation = toCoalesceToLocation(
    props.to !== undefined ? props.to : '',
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const backgroundLocation =
    toLocation.state?.backgroundLocation || calculatedBackgroundLocation
  const locale = useSelector(selectNullSafeLocaleFromState)

  const nextLocation = calculateNextLocation(toLocation, backgroundLocation)

  const ariaLabel =
    props.ariaLabelSource !== undefined
      ? translateSingleString(locale, props.ariaLabelSource)
      : props.ariaLabel

  return props.href !== undefined ? (
    <a
      id={props.id ? props.id : 'link'}
      data-testid={props.testId}
      tabIndex={props.tabIndex}
      href={props.href}
      className={props.className}
      css={props.css}
      aria-label={ariaLabel}
      target={'_blank'}
      rel={'noreferrer'}
      onClick={props.onClick}
      {...props.anchorProps}
    >
      {props.children}
    </a>
  ) : (
    <Link
      id={props.id ? props.id : 'link'}
      data-testid={props.testId}
      tabIndex={props.tabIndex}
      to={nextLocation}
      className={props.className}
      css={props.css}
      aria-label={ariaLabel}
      onClick={props.onClick}
      onKeyDown={props.onKeyPress}
    >
      {props.children}
    </Link>
  )
}
export default Lnk

interface RdrctProps {
  to: string | Location<LocationState>
  className?: string
}

export const Rdrct: React.FC<React.PropsWithChildren<RdrctProps>> = (
  props,
): React.ReactElement => {
  const toLocation = toCoalesceToLocation(props.to)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const backgroundLocation =
    toLocation.state?.backgroundLocation || calculatedBackgroundLocation

  const nextLocation = calculateNextLocation(toLocation, backgroundLocation)

  return <Redirect to={nextLocation} />
}

export const connectedPush = (to: string | Location<LocationState>) => {
  return (
    dispatch: ThunkDispatch<RootState, void, RouterAction>,
    getState: () => RootState,
  ): void => {
    const state = getState()

    const toLocation = toCoalesceToLocation(to)
    const calculatedBackgroundLocation =
      selectCalculatedBackgroundLocation(state)
    const backgroundLocation =
      toLocation.state?.backgroundLocation || calculatedBackgroundLocation

    dispatch(push(calculateNextLocation(toLocation, backgroundLocation)))
  }
}

export const connectedReplace = (to: string | Location<LocationState>) => {
  return (
    dispatch: ThunkDispatch<RootState, void, RouterAction>,
    getState: () => RootState,
  ): void => {
    const state = getState()

    const toLocation = toCoalesceToLocation(to)
    const calculatedBackgroundLocation =
      selectCalculatedBackgroundLocation(state)
    const backgroundLocation =
      toLocation.state?.backgroundLocation || calculatedBackgroundLocation

    dispatch(replace(calculateNextLocation(toLocation, backgroundLocation)))
  }
}

export const closeModal = () => {
  return (
    dispatch: ThunkDispatch<RootState, void, RouterAction>,
    getState: () => RootState,
  ): void => {
    const state = getState()
    dispatch(connectedPush(selectCalculatedBackgroundLocation(state)))
  }
}
